import { ProductionMethodsTab } from './ProductionMethodsTab';
import { EmissionsDataTab } from './EmissionsDataTab';
import React, { useEffect, useMemo, useRef } from 'react';
import { cn } from 'modules/Common/utils/cn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import { useCnGoods } from 'modules/Common/utils/Utility';
import { useState } from 'react';
import http from 'helpers/requests';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import productionMethods from '../data/ProdMethods.json';
import { notify, notifyError, AlertType } from 'modules/Common/ui/toast';
import useSupplierDetailsStore from 'modules/Suppliers/stores/supplierDetailsStore';
import useEmissionStore from 'modules/Suppliers/stores/emissionStore';
import Decimal from 'decimal.js';
import CgSupplierSelect from 'modules/Common/selectors/CgSupplierSelect';
import { useFetchSupplierInstallations } from '../hooks/useFetchSupplierInstallations';
import { CgFilterSelect } from 'modules/Common/ui/CgFilterDropdown';
import { Form, useCgForm, Label, sectionTitleClass } from 'modules/Common/form/v2';
import { zodResolver } from '@hookform/resolvers/zod';
import { emissionsFormSchema, ukEmissionsFormSchema } from './emissionsFormSchema';
import { useRegimeProfile } from 'modules/Common/importWizard/hooks/useRegimeProfile';
import { defaultFactorValidFrom } from 'modules/Common/utils/defaultFactorValidity';
import ukCbamScope from 'modules/UkImports/utils/ukCbamScope.data.json';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { useTranslation } from 'react-i18next';
import { resolveProductionMethods } from '../utils/resolveProductionMethods';
import { handleTablistKeys } from 'modules/Common/hooks/useTablistKeys';
import { anchorProps } from 'modules/Common/utils/gapAnchor';
import { isSupplierLevelGranularity } from 'modules/Common/utils/emissionGranularity';
import { useQueryClient } from '@tanstack/react-query';
import AssignImportsStep from 'modules/Common/ui/AssignImportsStep';
import { initialSelection, selectedLinesPayload } from 'modules/Common/utils/assignImports';
import {
  assignImports,
  invalidateImportQueries,
  previewAssignableImports,
} from 'modules/Common/utils/assignImports.api';

dayjs.extend(utc);

const valueTypeCodeMap = {
  NUMBER: '01',
  PERCENTAGE: '02',
  STRING: '03',
  RANGE: '04',
};

const normalizeValueType = (vt) => (vt == null ? '' : (valueTypeCodeMap[vt] ?? vt));

export const typesOfUnit = [
  { code: '01', description: 'Tonnes' },
  { code: '02', description: 'Kg' },
];

export const typesOfMeasurment = [
  { code: 'Weight', description: 'Weight' },
  { code: 'Energy', description: 'Energy' },
  { code: 'Money', description: 'Money' },
];

const defaultForm = {
  cnGood: '',
  indirectEmissionFactor: '',
  operatorGoodName: '',
  productionMethods: [],
  supplierId: '',
  installationId: '',
  typeOfApplicableReportingMethodology: 'TOM01',
  indirectDeterminationType: '01',
  indirectEmissionFactorSource: '01',
  electricityConsumed: '',
  electricitySource: 'SOE03',
  additionalInformation: '',
  date: '',
  lastRecordDate: '',
  unit: 'tCO2e/tonne of product',
  specificDirectEmission: '',
  specificIndirectEmission: '',
  otherSourceIndication: '',
  shareDefaultEmissions: 0,
  useBenchmarkDefault: true,
  benchmarkInputMode: 'Benchmark',
  benchmark: '',
  sefa: '',
  cbamFactor: '',
  reportingYear: '',
};

// Fields that belong to Tab 1 — validated before advancing to step 2
const TAB1_FIELDS = [
  'date',
  'lastRecordDate',
  'cnGood',
  'specificDirectEmission',
  'specificIndirectEmission',
  'indirectEmissionFactor',
  'indirectEmissionFactorSourceValue',
  'electricityConsumed',
  'electricitySource',
  'typeOfApplicableReportingMethodology',
  'indirectDeterminationType',
  'indirectEmissionFactorSource',
  'additionalInformation',
  'otherSourceIndication',
  'shareDefaultEmissions',
];

// The UK form's only tab (D13): no electricity breakdown, methodology or production methods.
const UK_TAB1_FIELDS = [
  'date',
  'lastRecordDate',
  'cnGood',
  'specificDirectEmission',
  'specificIndirectEmission',
];

// UK default factors carry a placeholder 2000-01-01 start, so an override of
// one seeds the day UK CBAM commences instead.
const UK_OVERRIDE_PERIOD_START = ukCbamScope.effectiveFrom;

// Build the full defaultValues object from a selectedEmission record.
// Exported for test: it is a pure function, and reaching it through a
// fully-mocked modal render would assert the mocks rather than the seeding.
export function buildDefaultsFrom(selectedEmission, effectiveSupplierId, { isUk = false } = {}) {
  const editingDefault = ['Global', 'Country'].includes(selectedEmission.granularity);

  if (editingDefault) {
    // The default generation serving 2026 was backdated to 2023-01-01
    // server-side. Seeding the form from the raw `date` would not just mislabel
    // the row — this form is POSTed, so an unadjusted submit would persist a new
    // Installation factor starting 2023-01-01.
    const validFrom = isUk ? UK_OVERRIDE_PERIOD_START : defaultFactorValidFrom(selectedEmission);

    return {
      ...defaultForm,
      productionMethods: [],
      cnGood:
        typeof selectedEmission.cnGood === 'object'
          ? selectedEmission?.cnGood?.cnCode
          : selectedEmission?.cnGood,
      supplierId: effectiveSupplierId,
      installationId: selectedEmission?.installationId ?? '',
      specificDirectEmission: String(selectedEmission?.emissionFactors?.direct ?? ''),
      specificIndirectEmission: String(selectedEmission?.emissionFactors?.indirect ?? ''),
      indirectEmissionFactorSource: '01',
      indirectEmissionFactorSourceValue: '',
      electricitySource: selectedEmission?.indirectMeta?.electricitySource ?? 'SOE01',
      typeOfApplicableReportingMethodology: 'TOM01',
      indirectDeterminationType: '01',
      date: validFrom ? dayjs.utc(validFrom).format('YYYY-MM-DD') : '',
      lastRecordDate: selectedEmission.lastRecordDate
        ? dayjs.utc(selectedEmission.lastRecordDate).format('YYYY-MM-DD')
        : '',
      shareDefaultEmissions: selectedEmission.shareDefaultEmissions ?? 0,
      useBenchmarkDefault: selectedEmission.sefa?.benchmarkSource === 'default',
      benchmarkInputMode: 'Benchmark',
      benchmark: String(selectedEmission.sefa?.benchmark ?? ''),
      sefa: String(selectedEmission.sefa?.totalSefa ?? ''),
      cbamFactor: String(selectedEmission.sefa?.cbamFactor ?? ''),
      reportingYear: String(selectedEmission.sefa?.year ?? ''),
    };
  }

  // Q1 (S7 fix batch 3) — root cause: the free-supplier publish/correct
  // contract carries only emissions{direct,indirect,total} (D-1), never the
  // electricity breakdown (electricityConsumed/indirectEmissionFactor) that
  // produced the indirect total. supplierFactorPublication.ts's
  // persistResolutionFactor reflects that on the legacy EmissionsFactor
  // projection this modal edits from (GET /emissions/operator/:id): it
  // hardcodes a PLACEHOLDER indirectMeta (determinationType actualValues,
  // electricitySource SOE03) with no real electricityConsumed/
  // indirectEmissionFactor. Left alone, that blank breakdown feeds straight
  // into EmissionsDataTab's auto-calculate effect — specificIndirectEmission
  // is ALWAYS derived from factor × consumption, never directly editable —
  // which WIPES the correctly-seeded published indirect number back to ''
  // the instant it sees blank electricity inputs, and the shared schema then
  // ALSO flags that as a required-field error (indirectDeterminationType
  // '01', actual values, genuinely the correct/honest classification here —
  // there's no legitimate alternate code for "actual total known, breakdown
  // just not on file" — requires electricityConsumed whenever it's '01').
  // Reconstruct a trivial, internally-consistent breakdown — consumption=1 ×
  // factor=(the published indirect) — so the auto-calc effect converges on
  // EXACTLY the published number instead of erasing it. 1 MWh/t is an
  // unmistakable placeholder, and the source-value note says outright that
  // it's preserved, not re-derived from real inputs; editing either
  // electricity field replaces the placeholder with a real one.
  const publishedIndirect = Number(selectedEmission.emissionFactors?.indirect ?? 0);
  const hasRealIndirectBreakdown = Boolean(
    selectedEmission.indirectMeta?.electricityConsumed ||
    selectedEmission.indirectMeta?.indirectEmissionFactor,
  );
  const reconstructIndirectBreakdown = !hasRealIndirectBreakdown && publishedIndirect > 0;

  return {
    ...defaultForm,
    _id: selectedEmission._id,
    cnGood:
      typeof selectedEmission.cnGood === 'object'
        ? selectedEmission.cnGood.cnCode
        : selectedEmission.cnGood,
    operatorGoodName: selectedEmission.operatorGoodName ?? '',
    productionMethods: (selectedEmission.productionMethods ?? []).map((m) => ({
      ...m,
      directParameters: (m.directParameters ?? []).map((p) => ({
        ...p,
        valueType: normalizeValueType(p.valueType),
      })),
    })),
    supplierId: selectedEmission.supplierId ?? effectiveSupplierId,
    installationId: selectedEmission?.installationId ?? '',
    specificDirectEmission: String(selectedEmission.emissionFactors?.direct ?? ''),
    specificIndirectEmission: String(selectedEmission.emissionFactors?.indirect ?? ''),
    indirectEmissionFactor: reconstructIndirectBreakdown
      ? String(publishedIndirect)
      : String(selectedEmission.indirectMeta?.indirectEmissionFactor ?? ''),
    indirectEmissionFactorSource:
      selectedEmission.indirectMeta?.indirectEmissionFactorSource ?? '01',
    indirectEmissionFactorSourceValue: reconstructIndirectBreakdown
      ? 'Preserved from your published record — not re-derived from an electricity breakdown.'
      : (selectedEmission.indirectMeta?.indirectEmissionFactorSourceValue ?? ''),
    electricityConsumed: reconstructIndirectBreakdown
      ? '1'
      : String(selectedEmission.indirectMeta?.electricityConsumed ?? ''),
    electricitySource: selectedEmission.indirectMeta?.electricitySource ?? 'SOE01',
    typeOfApplicableReportingMethodology:
      selectedEmission.directMeta?.typeOfApplicableReportingMethodology ?? 'TOM01',
    indirectDeterminationType: selectedEmission.indirectMeta?.determinationType ?? '01',
    otherSourceIndication: selectedEmission.indirectMeta?.otherSourceIndication ?? '',
    additionalInformation: selectedEmission.directMeta?.additionalInformation ?? '',
    unit: selectedEmission.unit ?? 'tCO2e/tonne of product',
    date: selectedEmission.date ? dayjs.utc(selectedEmission.date).format('YYYY-MM-DD') : '',
    lastRecordDate: selectedEmission.lastRecordDate
      ? dayjs.utc(selectedEmission.lastRecordDate).format('YYYY-MM-DD')
      : '',
    shareDefaultEmissions: selectedEmission?.shareDefaultEmissions ?? 0,
    useBenchmarkDefault: selectedEmission.sefa?.benchmarkSource === 'default',
    benchmarkInputMode: 'Benchmark',
    benchmark: String(selectedEmission.sefa?.benchmark ?? ''),
    sefa: String(selectedEmission.sefa?.totalSefa ?? ''),
    cbamFactor: String(selectedEmission.sefa?.cbamFactor ?? ''),
    reportingYear: String(selectedEmission.sefa?.year ?? ''),
  };
}

/**
 * The payload an EU importer sends — and every operator and free-supplier mount.
 */
function buildEuFactorPayload(values, { installationId, stockInstallationId }) {
  const productionMethods = values.productionMethods
    .filter((method) => method.methodCode !== '')
    .map((method) => ({
      ...method,
      directParameters: method.directParameters.filter(
        (parameter) => parameter.id !== '' || parameter.value !== '',
      ),
    }));

  return {
    ...values,
    installationId,
    ...(stockInstallationId ? { stockInstallationId } : {}),
    operatorGoodName: values.operatorGoodName,
    emissionFactors: {
      direct: new Decimal(values.specificDirectEmission).toNumber(),
      indirect: new Decimal(values.specificIndirectEmission).toNumber(),
      total: new Decimal(values.specificDirectEmission)
        .plus(new Decimal(values.specificIndirectEmission))
        .toNumber(),
    },
    directMeta: {
      determinationType: '01',
      typeOfApplicableReportingMethodology: values.typeOfApplicableReportingMethodology,
      additionalInformation: values.additionalInformation,
    },
    indirectMeta: {
      determinationType: values.indirectDeterminationType,
      indirectEmissionFactor: values.indirectEmissionFactor
        ? parseFloat(values.indirectEmissionFactor)
        : undefined,
      indirectEmissionFactorSource: values.indirectEmissionFactorSource,
      indirectEmissionFactorSourceValue: values.indirectEmissionFactorSourceValue,
      electricityConsumed: values.electricityConsumed
        ? parseFloat(values.electricityConsumed)
        : undefined,
      electricitySource: values.electricitySource,
      otherSourceIndication: values.otherSourceIndication,
    },
    productionMethods,
  };
}

/**
 * The payload a UK importer sends (backend docs/features/uk-cbam-emission-factors, D11–D14):
 * only what UK CBAM needs. The server stamps the determination and stores a blank indirect
 * as 0 — no methodology, electricity breakdown, production methods, SEFA or benchmark.
 */
function buildUkFactorPayload(values, { installationId }) {
  return {
    cnGood: values.cnGood,
    operatorGoodName: values.operatorGoodName,
    date: values.date,
    lastRecordDate: values.lastRecordDate,
    installationId,
    emissionFactors: {
      direct: new Decimal(values.specificDirectEmission).toNumber(),
      ...(values.specificIndirectEmission
        ? { indirect: new Decimal(values.specificIndirectEmission).toNumber() }
        : {}),
    },
  };
}
export default function InstallationRealEmissionsModal(props) {
  // A connected supplier's factor (D9): shown, locked, never submitted.
  const readOnly = Boolean(props.readOnly);
  const { t } = useTranslation('installations');
  const { selectedEmission } = useEmissionStore();
  const { supplier } = useSupplierDetailsStore();
  const cnGoods = useCnGoods();

  const httpClient = props.http || http;
  // REQUEST-RESULT toasts only. A caller overrides these when its own client
  // already reports the outcome — OperatorEmissionsModal passes no-ops because
  // its mutation hooks toast success/failure themselves.
  const notifyClient = props.notify || notify;
  const notifyErrorClient = props.notifyError || notifyError;
  // The modal's OWN client-side messages (missing supplier, validation) never
  // route through that override: nothing was sent, so no caller's hook can
  // have reported them. Routing them through a no-op is what made Submit read
  // as a dead button in the operator flows — the diagram precursor node's
  // "+ Create" aborted on the supplier guard below with no visible trace.
  const notifySelf = notify;

  // UK variant (backend docs/features/uk-cbam-emission-factors, S7; D11–D13). Only the
  // importer (declarant) mounts ask; the operator and free-supplier forms stay EU. A profile
  // that cannot be read falls back to the EU form — the server strips EU-only fields for a
  // UK importer anyway (S1).
  const isDeclarantContext = props.contextType === 'declarant';
  const { profile: regimeProfile, isReady: regimeReady } = useRegimeProfile({
    enabled: Boolean(props.show) && isDeclarantContext,
  });
  const isUk = isDeclarantContext && regimeProfile?.regime === 'UK';
  const formReady = !isDeclarantContext || regimeReady;
  const tab1Fields = isUk ? UK_TAB1_FIELDS : TAB1_FIELDS;
  // The schema is chosen at validation time, so a regime that arrives after mount applies.
  const isUkRef = useRef(isUk);
  isUkRef.current = isUk;

  const methods = useCgForm({
    resolver: (values, context, options) =>
      zodResolver(isUkRef.current ? ukEmissionsFormSchema : emissionsFormSchema)(
        values,
        context,
        options,
      ),
    defaultValues: defaultForm,
    mode: 'onTouched',
  });

  const [categoryProductionMethods, setCategoryProductionMethods] = useState([]);
  const [step, setStep] = useState('1');
  const appliedStepRequestRef = useRef(null);
  const [loading, setLoading] = useState(false);
  // The "Assign installation to imports" step (backend
  // docs/features/import-installation-backfill): set when a new factor covers
  // supplier-only imports — `{ data, preview, selected }` — and the dialog shows
  // the step instead of the form until the user confirms or goes back.
  const [assign, setAssign] = useState(null);
  const queryClient = useQueryClient();
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [selectedInstallationId, setSelectedInstallationId] = useState('');
  const [installationError, setInstallationError] = useState('');

  const navigationData = props.navigationData;

  // contextType="operator" means the caller routes writes through the operator
  // endpoints (POST /emissions/operator, PATCH /emissions/operator/:id), which
  // derive ownership from operatorId + installationId server-side.
  const isOperatorContext = props.contextType === 'operator';

  // An importer can no longer keep a supplier-level factor (D20/D24 in the backend's
  // docs/features/uk-cbam-emission-factors). Editing one therefore always shows the
  // installation picker, locked to the factor's OWN supplier so the edit cannot move
  // it — whichever table opened the modal. Operator surfaces pass their installation
  // directly and are left as they are.
  const isSupplierLevelEdit =
    props.contextType === 'declarant' && isSupplierLevelGranularity(selectedEmission?.granularity);
  const showSupplierSelector = Boolean(props.showSupplierSelector) || isSupplierLevelEdit;
  // The factor's own supplier comes FIRST on a supplier-level edit (D24): a caller's lock
  // must never widen the choice to another supplier's installations.
  const factorSupplierId = selectedEmission?.supplierId ?? selectedEmission?.supplierData?._id;
  const lockedSupplierId = isSupplierLevelEdit
    ? factorSupplierId || props.lockedSupplierId
    : props.lockedSupplierId;

  const getEffectiveSupplierId = () => {
    if (lockedSupplierId) {
      return lockedSupplierId;
    }
    if (showSupplierSelector && selectedSupplierId) {
      return selectedSupplierId;
    }
    return supplier?._id;
  };

  // Which SUPPLIER the submitted factor belongs to, in priority order:
  //
  //   1. the record itself, when editing one;
  //   2. `installation.operatorId` — a CbamSupplier ref that comes back
  //      populated from GET /installations/operator/:id and as a raw id from
  //      GET /stock/installations/:id, so unwrap both shapes (same handling as
  //      OperatorInstallations/components/InstallationRailCards.jsx:57);
  //   3. the declarant chain (locked id → supplier selector → supplier-details
  //      store). The store tail is skipped under contextType="operator": it is
  //      only ever written by the declarant supplier detail PAGE, so on an
  //      operator surface it is empty at best and stale at worst.
  //
  // Undefined is a legitimate answer for an operator's OWN installation — see
  // the guard in handleSubmit.
  const resolveSubmitSupplierId = () => {
    if (selectedEmission?.supplierId) return selectedEmission.supplierId;

    const installationSupplierRef = props.installation?.operatorId;
    const installationSupplierId = installationSupplierRef?._id ?? installationSupplierRef;
    if (installationSupplierId) return installationSupplierId;

    if (isOperatorContext) return lockedSupplierId || undefined;
    return getEffectiveSupplierId();
  };

  // When the caller locks the supplier (e.g. opening the modal from a supplier
  // detail page), keep selectedSupplierId in sync so the supplier-select shows
  // the locked value and downstream effects (installation fetch, form supplier
  // field) resolve correctly.
  useEffect(() => {
    if (props.show && lockedSupplierId) {
      setSelectedSupplierId(lockedSupplierId);
    }
  }, [props.show, lockedSupplierId]);

  // Preselect the supplier/installation pickers from the emission record when
  // the modal opens to convert a default. The declarant Emissions table joins
  // supplierData/installationData onto each row, so we pull the ids from there
  // first and fall back to the flat fields. Skipped when the supplier is
  // locked — that branch handles its own initialization.
  useEffect(() => {
    if (!selectedEmission || !showSupplierSelector || lockedSupplierId) return;

    const initialSupplierId =
      selectedEmission.supplierId ?? selectedEmission.supplierData?._id ?? '';
    if (initialSupplierId) {
      setSelectedSupplierId(initialSupplierId);
    }

    const initialInstallationId =
      selectedEmission.installationId ?? selectedEmission.installationData?._id ?? '';
    if (initialInstallationId) {
      setSelectedInstallationId(initialInstallationId);
    }
  }, [selectedEmission, showSupplierSelector, lockedSupplierId]);

  const { data: supplierInstallations = [], isLoading: isLoadingInstallations } =
    useFetchSupplierInstallations(getEffectiveSupplierId(), {
      // Only while open: the supplier tab mounts this modal closed with the picker enabled.
      enabled: Boolean(props.show) && !!getEffectiveSupplierId() && showSupplierSelector,
    });

  const showProp = props.show;
  const initialStepProp = props.initialStep;
  const stepRequestId = props.stepRequestId;
  const routesAvailable = categoryProductionMethods.length > 0;
  useEffect(() => {
    if (!showProp) {
      appliedStepRequestRef.current = null;
      return;
    }
    if (!initialStepProp || appliedStepRequestRef.current === stepRequestId) return;
    if (initialStepProp === '1') {
      appliedStepRequestRef.current = stepRequestId;
      setStep('1');
      return;
    }
    if (initialStepProp === '2' && !isUk && routesAvailable) {
      appliedStepRequestRef.current = stepRequestId;
      setStep('2');
    }
  }, [showProp, initialStepProp, stepRequestId, isUk, routesAvailable]);

  const onClose = () => {
    props.onClose();
    setStep('1');
    setLoading(false);
    setAssign(null);
    methods.reset(defaultForm);
    setSelectedSupplierId('');
    setSelectedInstallationId('');
    setInstallationError('');
  };

  // Populate form when editing an existing emission
  useEffect(() => {
    // Same resolution the submit uses, so the form's `supplierId` — which
    // EmissionsDataTab sends to /custom-benchmarks/search — can never disagree
    // with what gets posted, and never picks up a stale declarant supplier on
    // an operator surface.
    const effectiveSupplierId = resolveSubmitSupplierId();

    if (selectedEmission) {
      methods.reset(buildDefaultsFrom(selectedEmission, effectiveSupplierId, { isUk }));

      if (showSupplierSelector && selectedEmission?.installationId) {
        setSelectedInstallationId(selectedEmission.installationId);
      }

      // Assign unconditionally. Guarding this on success let an unresolvable
      // good keep the previously opened good's methods — silently offering
      // methods from the wrong category rather than failing visibly.
      setCategoryProductionMethods(
        resolveProductionMethods(selectedEmission.cnGood, cnGoods, productionMethods),
      );
    } else if (!selectedEmission && showSupplierSelector) {
      methods.setValue('supplierId', effectiveSupplierId ?? '');
    }
  }, [selectedEmission, supplier, cnGoods, selectedSupplierId, isUk]);

  // Handle navigation data from shipments to pre-fill form
  useEffect(() => {
    if (navigationData && showSupplierSelector && !selectedEmission) {
      if (navigationData.supplierId) setSelectedSupplierId(navigationData.supplierId);
      if (navigationData.installationId) setSelectedInstallationId(navigationData.installationId);
      if (navigationData.cnGoodId) {
        methods.setValue('cnGood', navigationData.cnGoodId);
        if (navigationData.supplierId) methods.setValue('supplierId', navigationData.supplierId);
        if (navigationData.installationId)
          methods.setValue('installationId', navigationData.installationId);
      }
    }
  }, [navigationData, showSupplierSelector, selectedEmission]);

  // Populate category production methods whenever the CN good changes.
  //
  // This used to read the value imperatively and rely on CnGoodField.onChange
  // to cover user-driven changes. That left a gap: when the CN good arrives by
  // prefill instead of by picking it in the dropdown — which is how the
  // installation and supplier flows open the form — onChange never fires, so
  // nothing populated the list and the Production Methods tab had nothing to
  // offer. Watching the value covers both routes.
  const watchedCnGood = methods.watch('cnGood');
  useEffect(() => {
    const cnGood = watchedCnGood;
    if (!cnGood || !cnGoods?.length) return;

    const methodsForCategory = resolveProductionMethods(cnGood, cnGoods, productionMethods);
    if (!methodsForCategory.length) return;

    const methodsChanged =
      methodsForCategory.length !== categoryProductionMethods.length ||
      methodsForCategory.some((method, idx) => method.id !== categoryProductionMethods[idx]?.id);

    if (methodsChanged) {
      setCategoryProductionMethods(methodsForCategory);
    }

    // Seed a first row whenever the form has none. This was previously skipped
    // for anything carrying an `_id`, which excluded default factors — they
    // always carry one (it is what buildDefaultsFrom seeds from) while their
    // productionMethods are deliberately empty. So the one flow that most needs
    // a starting row, converting a default into actual data, never got one.
    const currentProductionMethods = methods.getValues('productionMethods');
    if (currentProductionMethods.length === 0) {
      methods.setValue('productionMethods', [
        {
          methodCode: methodsForCategory[0].id,
          name: methodsForCategory[0].name,
          steelMill: '',
          directParameters: [],
        },
      ]);
    }
  }, [cnGoods, watchedCnGood, categoryProductionMethods.length, selectedEmission]);

  const handleSubmit = async (values, event) => {
    // When this modal is opened on top of another form (e.g. StockPurchaseOrderModal),
    // React's synthetic submit event bubbles through the portal to the outer form and
    // triggers its submission too. Stop it here.
    event?.stopPropagation();

    // The tab bar can reach this step without passing handleNextStep, so the
    // installation rule (backend D20) is enforced here as well.
    if (showSupplierSelector && !selectedInstallationId) {
      setInstallationError(t('fields.installationRequired', 'Installation is required'));
      setStep('1');
      return;
    }
    const submitSupplierId = resolveSubmitSupplierId();
    // When the picker is shown, what it holds is the answer — an edit may move a factor to
    // another installation of the same supplier. `||` because an unset picker is ''.
    const installationId =
      (showSupplierSelector && selectedInstallationId) ||
      selectedEmission?.installationId ||
      props.installation?._id ||
      selectedInstallationId;

    let data = isUk
      ? buildUkFactorPayload(values, { installationId })
      : buildEuFactorPayload(values, {
          installationId,
          stockInstallationId: props.stockInstallationId,
        });

    // `defaultForm` seeds supplierId as '', which is not a supplier — carry the
    // key only when it actually resolves, so an operator's own-installation
    // factor goes out supplier-less rather than with an empty string.
    if (submitSupplierId) {
      data.supplierId = submitSupplierId;
    } else {
      delete data.supplierId;
    }

    // UK CBAM has no SEFA or benchmark (D5), even on a grandfathered factor that carries one.
    if (!isUk && Boolean(values.sefa)) {
      const sefaDecimal = new Decimal(values.sefa);
      const cbamFactorDecimal = new Decimal(values.cbamFactor);
      const benchmarkDecimal = values.benchmark
        ? new Decimal(values.benchmark)
        : sefaDecimal.div(cbamFactorDecimal.div(100));
      data.sefa = {
        benchmark: benchmarkDecimal.toNumber(),
        benchmarkSource: values.useBenchmarkDefault ? 'default' : 'manual',
        productionSefa: sefaDecimal.toNumber(),
        precursorsSefa: 0,
        totalSefa: sefaDecimal.toNumber(),
        cbamFactor: cbamFactorDecimal.toNumber(),
        year: parseInt(values.reportingYear),
      };

      // Signal backend to upsert CustomBenchmark for declarant context
      if (props.contextType === 'declarant') {
        data.createCustomBenchmark = true;
      }
    } else {
      delete data.sefa;
      delete data.benchmark;
    }

    const editingDefault =
      selectedEmission && ['Global', 'Country'].includes(selectedEmission.granularity);

    // The DECLARANT routes (POST /emissions/, PATCH /emissions/:id) scope a
    // factor to a supplier, and converting a default always POSTs a new one —
    // a default carries no supplierId of its own, so the chain above leans on
    // the installation's operator; on an installation with no operator the
    // supplier-details store is empty on this page and we would POST a
    // supplier-less factor that prices nothing.
    //
    // The OPERATOR routes are the opposite case, and requiring a supplier here
    // is what broke manual entry on an operator's own installations. Those
    // sites are owned via `independentOperatorId` (an operator organisation),
    // not `operatorId` (a CbamSupplier) — there IS no supplier, by design.
    // POST /emissions/operator resolves operatorId from installationId itself
    // (backend api/emissions.ts:3651) and never persists a supplierId; the
    // declarant search pipeline explicitly handles "operator-managed emissions
    // (which lack supplierId)" (api/emissions.ts:2203). A supplier is only in
    // play on the operator surface when the installation belongs to one of the
    // operator's suppliers (stock installations / diagram precursors), and that
    // case resolves above rather than falling through to here.
    if (!data.supplierId && !isOperatorContext) {
      setLoading(false);
      notifySelf(
        t('emissions.supplierRequiredTitle', 'Supplier required'),
        t(
          'emissions.supplierRequiredBody',
          'This installation has no operator on record, so we cannot tell which supplier the new emission factor belongs to. Set the installation operator first.',
        ),
        AlertType.ERROR,
      );
      return;
    }

    // closeOnSuccess (default true, backward compatible): lets a caller keep
    // the dialog open after a successful submit instead of closing it — the
    // free-supplier "provide data" flow (DataRequestsPage) re-seeds this same
    // modal instance for the request's NEXT line rather than closing between
    // lines. handleRefresh is awaited so async callers (queue advancement,
    // cache invalidation) complete inside this try/catch — loading stays on
    // and failures surface here instead of escaping fire-and-forget; awaiting
    // a sync callback is a no-op for every other caller.
    setLoading(true);
    // Editing a saved factor PATCHes it; converting a default POSTs a new one.
    const editId = selectedEmission?._id && !editingDefault ? selectedEmission._id : null;

    // A declarant's new or edited factor may cover supplier-only imports of this
    // installation's supplier (D1) — an edit can move its period, CN code or
    // installation. If it does, confirm which of them get the installation before
    // anything is saved (D3). A failed lookup never blocks the save — the factor is
    // what the user asked for.
    if (isDeclarantContext) {
      let preview = null;
      try {
        preview = await previewAssignableImports(httpClient, data);
      } catch (err) {
        // Logged so a missing step is traceable; the save goes ahead regardless.
        console.warn('Assignable-imports preview failed', err);
        preview = null;
      }
      if (preview?.lines?.length > 0) {
        setAssign({ data, editId, preview, selected: initialSelection(preview.lines) });
        setLoading(false);
        return;
      }
    }
    await saveFactor(data, editId);
  };

  // Save the factor (POST, or PATCH when `editId`), then assign its installation to
  // the chosen imports.
  const saveFactor = async (data, editId, lines = []) => {
    setLoading(true);
    let factorId = editId;
    try {
      if (editId) {
        await httpClient.patch(`/emissions/${editId}`, data);
      } else {
        factorId = (await httpClient.post('/emissions/', data))?.data?._id;
      }
    } catch (err) {
      // Nothing was saved: back to the form, whose field errors are only visible there.
      notifyErrorClient(err);
      setAssign(null);
      setLoading(false);
      return;
    }
    try {
      let assigned = 0;
      let skipped = 0;
      let kept = 0;
      if (lines.length > 0 && factorId) {
        try {
          ({ assigned, skipped, kept } = await assignImports(httpClient, factorId, lines));
          invalidateImportQueries(queryClient);
        } catch (err) {
          // The factor is saved; only the assignment failed, and the error says why.
          notifyErrorClient(err);
        }
      }
      await props.handleRefresh();
      if (props.closeOnSuccess !== false) onClose();
      else setAssign(null);
      if (editId) {
        notifyClient(
          t('common:toasts.success', 'Success'),
          t('emissionsTable.updatedToast', 'Emission has been updated successfully'),
          AlertType.SUCCESS,
        );
        if (assigned > 0) {
          notifyClient(
            t('assignImports.title', 'Assign installation to imports'),
            t('assignImports.assignedToast', {
              count: assigned,
              defaultValue: '{{count}} import now uses actual values.',
              defaultValue_other: '{{count}} imports now use actual values.',
            }),
            AlertType.SUCCESS,
          );
        }
      } else {
        notifyClient(
          t('common:toasts.success', 'Success'),
          assigned > 0
            ? t('assignImports.createdAndAssignedToast', {
                count: assigned,
                defaultValue:
                  'Emission has been created successfully. {{count}} import now uses its actual values.',
                defaultValue_other:
                  'Emission has been created successfully. {{count}} imports now use its actual values.',
              })
            : props.createSuccessMessage ||
                t('emissionsTable.createdToast', 'Emission has been created successfully'),
          AlertType.SUCCESS,
        );
      }
      if (kept > 0) {
        notifyClient(
          t('assignImports.keptTitle', 'Choose the product on some imports'),
          t('assignImports.keptBody', {
            count: kept,
            defaultValue:
              "{{count}} import got the installation but keeps its current values: the installation has several products for this CN good and the import doesn't say which.",
            defaultValue_other:
              "{{count}} imports got the installation but keep their current values: the installation has several products for this CN good and the imports don't say which.",
          }),
          AlertType.WARNING,
        );
      }
      if (skipped > 0) {
        notifyClient(
          t('assignImports.skippedTitle', 'Some imports were not assigned'),
          t('assignImports.skippedBody', {
            count: skipped,
            defaultValue: '{{count}} import could not be assigned and keeps its current values.',
            defaultValue_other:
              '{{count}} imports could not be assigned and keep their current values.',
          }),
          AlertType.WARNING,
        );
      }
    } catch (err) {
      notifyErrorClient(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvalid = (errors, event) => {
    event?.stopPropagation();
    console.log('[InstallationRealEmissionsModal] zod validation errors blocking submit:', errors);
    const hasTab1Error = tab1Fields.some((f) => errors[f]);
    if (hasTab1Error) {
      setStep('1');
      notifySelf(
        t('emissionsTable.validationErrorTitle', 'Validation Error'),
        t(
          'emissionsTable.validationErrorBody',
          'Please fix the highlighted fields in Emissions Data before submitting.',
        ),
        AlertType.ERROR,
      );
    } else {
      // Everything else lives on tab 2 (productionMethods). Without this the
      // submit died with nothing but the console line above — the button
      // looked broken while a required production method sat one tab away.
      notifySelf(
        t('emissionsTable.validationErrorTitle', 'Validation Error'),
        t(
          'production.validationErrorBody',
          'Please fix the highlighted fields in Production Methods before submitting.',
        ),
        AlertType.ERROR,
      );
    }
  };

  const handleNextStep = async () => {
    if (step == 1) {
      if (showSupplierSelector && !selectedInstallationId) {
        setInstallationError(t('fields.installationRequired', 'Installation is required'));
        await methods.trigger(tab1Fields);
        return;
      }
      const valid = await methods.trigger(tab1Fields);
      if (valid) setStep('2');
    }
  };

  const handlePreviousStep = () => {
    if (+step <= 1) return;
    setStep((prev) => +prev - 1);
  };

  const handleInstallationChange = (option) => {
    setSelectedInstallationId(option?.value ?? '');
    if (option?.value) setInstallationError('');
  };

  const installationOptions = useMemo(
    () =>
      (supplierInstallations ?? [])
        // D24: converting a supplier-level factor may only pick its OWN supplier's
        // installations — the backend refuses any other link (parent, operator).
        .filter(
          (inst) =>
            !isSupplierLevelEdit ||
            String(inst.operatorId?._id ?? inst.operatorId) === String(factorSupplierId),
        )
        .map((inst) => ({ value: inst._id, label: inst.name })),
    [supplierInstallations, isSupplierLevelEdit, factorSupplierId],
  );

  const assignFooter = assign && (
    <div className="flex justify-end gap-2">
      <CgRadixButton
        variant="secondary"
        disabled={loading}
        onClick={() => setAssign(null)}
      >
        {t('common:actions.back', 'Back')}
      </CgRadixButton>
      <CgRadixButton
        variant="primary"
        loading={loading}
        loadingLabel={t('common:actions.saving', 'Saving…')}
        onClick={() =>
          saveFactor(
            assign.data,
            assign.editId,
            selectedLinesPayload(assign.preview.lines, assign.selected),
          )
        }
      >
        {assign.selected.size > 0
          ? t('assignImports.saveAndAssign', {
              count: assign.selected.size,
              defaultValue: 'Save & assign {{count}} import',
              defaultValue_other: 'Save & assign {{count}} imports',
            })
          : t('assignImports.saveFactorOnly', 'Save factor only')}
      </CgRadixButton>
    </div>
  );

  const footer = (
    <div className="flex justify-end gap-2">
      <CgRadixButton
        variant="ghost"
        onClick={onClose}
      >
        {t('common:actions.close', 'Close')}
      </CgRadixButton>
      {+step > 1 && step != 3 && (
        <CgRadixButton
          variant="secondary"
          disabled={loading}
          onClick={handlePreviousStep}
          icon={faIcons.faArrowLeft}
        >
          {t('common:actions.previous', 'Previous')}
        </CgRadixButton>
      )}
      {/* The UK form has one tab, so it submits from it. */}
      {formReady && !isUk && +step < 2 && (
        <CgRadixButton
          variant="primary"
          onClick={handleNextStep}
        >
          {t('common:actions.next', 'Next')}{' '}
          <FontAwesomeIcon
            icon={faIcons.faArrowRight}
            className="w-3.5 h-3.5"
          />
        </CgRadixButton>
      )}
      {formReady && !readOnly && (isUk || step == 2) && (
        <CgRadixButton
          variant="primary"
          type="submit"
          form="emissions-form"
          loading={loading}
          loadingLabel={t('common:actions.saving', 'Saving…')}
          icon={faIcons.faSave}
          // Additive, optional — undefined for every existing caller. The
          // free-supplier "provide data" flow (SupplierEmissionsModal's
          // installationChoice) uses this to block the actual save until its
          // required target-installation selector has a value, without this
          // shared modal knowing anything about that caller's domain.
          disabled={props.submitDisabled}
        >
          {props.submitLabel || t('actions.submit', 'Submit')}
        </CgRadixButton>
      )}
    </div>
  );

  return (
    <CgDialog
      open={props.show}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={
        assign
          ? t('assignImports.title', 'Assign installation to imports')
          : t('emissions.title', 'Emissions Data')
      }
      description={
        assign
          ? [assign.preview.installation?.name, supplier?.name].filter(Boolean).join(' · ')
          : props.description
      }
      width={assign ? 800 : 1100}
      closeOnOverlayClick={false}
      footer={assign ? assignFooter : footer}
    >
      {assign && (
        <AssignImportsStep
          preview={assign.preview}
          isUk={isUk}
          factor={{
            cnGood: assign.data.cnGood,
            cnName: cnGoods?.find?.((good) => good._id === assign.data.cnGood)?.name,
            date: assign.data.date,
            lastRecordDate: assign.data.lastRecordDate,
            direct: assign.data.emissionFactors?.direct,
            indirect: assign.data.emissionFactors?.indirect ?? 0,
            total:
              assign.data.emissionFactors?.total ??
              Number(assign.data.emissionFactors?.direct ?? 0) +
                Number(assign.data.emissionFactors?.indirect ?? 0),
          }}
          selected={assign.selected}
          onSelectionChange={(selected) => setAssign((prev) => ({ ...prev, selected }))}
        />
      )}
      <Form
        methods={methods}
        // Read-only (a connected supplier's factor, D9): nothing to submit.
        onSubmit={readOnly ? () => {} : handleSubmit}
        onInvalid={handleInvalid}
        id="emissions-form"
      >
        <div
          className={cn('text-sm', assign && 'hidden')}
          style={{ minHeight: '40vh' }}
        >
          {/* Optional caller-owned MESSAGE rendered above the form. Distinct
              from `modeControl` below, which is a control the caller expects
              the operator to act on; this is context about the form's own
              starting state. The precursor override uses it to say which of
              the prefilled numbers came from the node and which could not.
              Undefined by default so every other caller is unaffected. */}
          {props.prependContent}
          {/* Optional caller-owned control rendered above the form — the
              free-supplier wrapper uses this for its "Save as draft" /
              "Publish now" choice (+ a Skip action inside a multi-line
              request flow). Undefined by default so every other caller is
              unaffected. */}
          {props.modeControl}
          {showSupplierSelector && (
            <section className="mb-4 p-3 rounded-xl border border-slate-200 bg-slate-50">
              <p className={sectionTitleClass}>
                {t('fields.supplierAndInstallation', 'Supplier & Installation')}
              </p>
              <div className="grid grid-cols-12 gap-4">
                {/* Supplier */}
                <div className="col-span-12 md:col-span-6">
                  <Label
                    htmlFor="supplierSelector"
                    required
                  >
                    {t('fields.supplier', 'Supplier')}
                  </Label>
                  <CgSupplierSelect
                    selectedSupplier={selectedSupplierId}
                    setSelectedSupplier={(value) => {
                      setSelectedSupplierId(value);
                      setSelectedInstallationId('');
                    }}
                    disabled={Boolean(lockedSupplierId)}
                  />
                </div>

                {/* Installation */}
                <div className="col-span-12 md:col-span-6">
                  <Label
                    htmlFor="installationId"
                    required
                  >
                    {t('fields.installation', 'Installation')}
                  </Label>
                  <div className="relative">
                    <CgFilterSelect
                      options={installationOptions}
                      value={selectedInstallationId || null}
                      onChange={(val) =>
                        handleInstallationChange(
                          val ? installationOptions.find((o) => o.value === val) : null,
                        )
                      }
                      disabled={isLoadingInstallations || !getEffectiveSupplierId()}
                      placeholder={
                        getEffectiveSupplierId()
                          ? t('fields.selectInstallationPlaceholder', 'Select an Installation')
                          : t('fields.selectSupplierFirstPlaceholder', 'Select a supplier first')
                      }
                      error={Boolean(installationError)}
                      // Entity picker — always searchable, regardless of list size.
                      searchable
                    />
                    {installationError && (
                      <p className="text-red-500 text-xs mt-1">{installationError}</p>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Nothing of the form mounts before the regime is known: EmissionsDataTab's EU
              effects (the indirect auto-calc, the benchmark lookups) would otherwise run
              against a form that is about to become the UK one and overwrite a seeded
              indirect. Operator and free-supplier mounts are ready on the first render. */}
          {/* Underline tabs (ex react-bootstrap Tabs). Both panes stay MOUNTED
              and toggle via `hidden`, exactly as react-bootstrap kept inactive
              panes mounted — EmissionsDataTab's auto-calculate effects are
              load-bearing while tab 2 is shown (see the indirectMeta note
              above), so conditional rendering would change behavior. */}
          {formReady ? (
            <>
              <div
                role="tablist"
                onKeyDown={handleTablistKeys}
                id="emissions-tabs"
                className="mb-4 flex gap-1 border-b border-gray-200 text-sm"
                {...anchorProps('recordModal', 'emissionFactor')}
              >
                {[
                  { key: '1', title: t('emissions.title', 'Emissions Data'), disabled: false },
                  /* Production methods are EU-only (D5, D13). */
                  ...(isUk
                    ? []
                    : [
                        {
                          key: '2',
                          title: t(
                            'production.methodsTitle',
                            'Production Methods & Qualifying Parameters',
                          ),
                          disabled: categoryProductionMethods.length === 0,
                        },
                      ]),
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={step === tab.key}
                    disabled={tab.disabled}
                    onClick={() => setStep(tab.key)}
                    {...(tab.key === '2' ? anchorProps('recordTab', 'routes') : {})}
                    className={cn(
                      '-mb-px border-b-2 border-transparent px-4 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:bg-secondaryColor-lightest hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent',
                      step === tab.key && 'text-primaryColor border-primaryColor bg-transparent',
                    )}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>
              <div
                role="tabpanel"
                className={cn(step !== '1' && 'hidden')}
              >
                <EmissionsDataTab
                  setCategoryProductionMethods={setCategoryProductionMethods}
                  lockForm={readOnly}
                  contextType={props.contextType}
                  isUk={isUk}
                  installationId={props.installationId || props.installation?._id}
                  operatorId={props.operatorId}
                />
              </div>
              {!isUk && (
                <div
                  role="tabpanel"
                  className={cn(step !== '2' && 'hidden')}
                >
                  <ProductionMethodsTab
                    categoryProductionMethods={categoryProductionMethods}
                    loading={loading}
                    lockForm={readOnly}
                  />
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-slate-500 mb-2">{t('common:states.loading', 'Loading')}</p>
          )}
        </div>
      </Form>
    </CgDialog>
  );
}
